/**
 * Pet Parent Hub — Pre-Surrender resource library + adopter-question content.
 *
 * Ported from the "Pre-Surrender - Resources + Question Logic" sheet (Guide
 * Details + Typeform - Reasons for Rehoming tabs) and the Claude Design
 * "Pre-Surrender Homepage" handoff. Reason keys are the EXACT choice labels the
 * enrollment Typeforms store in `surrender_reason` (see
 * scripts/update-enrollment-typeforms.js) — anything else (native-Other free
 * text, or nothing recorded) resolves to the universal "Other" experience.
 *
 * `{pet}` tokens are replaced at render time with the pre-surrender animal's
 * name (parent-supplied via Typeform, unlike shelter-sourced adopter names).
 */

export interface PreSurrenderGuide {
  /** In-hub lesson slug — rows link to /hub/lessons-and-guides/<slug>. */
  slug: string;
  title: string;
  /** Accordion row heading ("Guide Section Header" in the sheet). */
  header: string;
  /** One-liner shown in the Next Up callout. */
  summary: string;
  bullets: string[];
}

export interface QuestionGroup {
  reason: string;
  intro: string;
  questions: string[];
}

const guide = (
  slug: string,
  title: string,
  header: string,
  summary: string,
  bullets: string[],
): PreSurrenderGuide => ({
  slug, title, header, summary, bullets,
});

export const DOG_GUIDES: PreSurrenderGuide[] = [
  guide(
'aggression',
'Managing Aggression',
'Learn to Manage Aggressive Behavior',
'Keep everyone safe while you work on the underlying cause',
[
      'Identify the situations that trigger the behavior and manage those situations carefully',
      'Use gates, leashes, and separation to prevent the behavior from being repeated',
      'Discuss the behavior with your veterinarian',
    ],
),
  guide(
'understanding-and-managing-stranger-danger',
'Understanding & Managing Stranger Danger',
'Help {pet} Feel Safer Around New People',
'Understand your dog’s signals and create more comfortable interactions with people',
[
      'Learn the body language that comes before growling or snapping',
      'Ask visitors to ignore your dog',
      'Manage greetings with a leash, gate, or separate room',
    ],
),
  guide(
'keeping-dogs-and-kids-safe',
'Keeping Dogs & Kids Safe',
'Help {pet} and Kids Feel Safe Together',
'Support safe, positive interactions between your dog and children',
[
      'Supervise all interactions between your dog and young children',
      'Use gates, x-pens, and crates to create safe separation',
      'Give your dog a kid-free space where they can retreat and relax',
    ],
),
  guide(
'leash-reactivity',
'Understanding & Managing Leash Reactivity',
'Help {pet} Stay Calm on Leash',
'Make walks calmer, safer, and more enjoyable',
[
      'Use a well-fitted harness for better control',
      'Reward check-ins and a loose leash',
      'Create distance from triggers before your dog reacts',
    ],
),
  guide(
'separation-anxiety',
'Separation Anxiety',
'Ease {pet}\'s Anxiety About Being Alone',
'Reduce stress and build confidence during alone time',
[
      'Discuss the behavior with your veterinarian',
      'Start with short absences and build up gradually',
      'Leave a food puzzle or chew at departures',
    ],
),
  guide(
'building-confidence-outside-the-home',
'Building Confidence Outside the Home',
'Build {pet}\'s Confidence Outside the Home',
'Balance play, exposure, and safety',
[
      'Choose calm, low-traffic settings over crowded dog parks',
      'Let your dog explore at their own pace without forcing interactions',
      'Reward calm behavior and check-ins with you around new people, dogs, and environments',
    ],
),
  guide(
'managing-high-energy',
'Managing High Energy',
'Give {pet} an Outlet for Extra Energy',
'Channel energy into healthy, productive outlets',
[
      'Build in daily physical exercise and time to sniff',
      'Use puzzle feeders and training games for mental work',
      'Teach and reward settling, not just activity',
    ],
),
  guide(
'barking',
'Understanding & Managing Barking',
'Reduce {pet}\'s Barking',
'Understand why the barking happens and reduce how often it does',
[
      'Identify what triggers your dog’s barking',
      'Reduce exposure to triggers when possible, such as by blocking window views or masking outside sounds',
      'Reinforce calm, quiet behavior and teach an alternative behavior, like going to a mat',
    ],
),
  guide(
'house-training',
'House Training',
'Set {pet} Up for House Training Success',
'Create a consistent routine for reliable bathroom habits',
[
      'Talk to your veterinarian to rule out medical causes for accidents',
      'Take your dog out on a consistent schedule',
      'Clean accidents with an enzymatic cleaner',
    ],
),
  guide(
'managing-conflict-in-dog-cat-households',
'Managing Conflict in Dog-Cat Households',
'Ease Tension Between {pet} and Cats',
'Support calm, positive interactions between dogs and cats in the home',
[
      "Give your cat vertical space and escape routes your dog can't reach",
      'Use gates and closed doors to keep everyone safe',
      'Interrupt chasing or fixation before it escalates',
    ],
),
  guide(
'relaxation-for-dogs-around-cats',
'Relaxation for Dogs Around Cats',
'Teach {pet} to Relax Around Cats',
'Teach your dog to settle in the same room as a cat',
[
      'Start with your cat at a distance where your dog can stay relaxed',
      'Reward your dog for settling on a mat while your cat moves around',
      'Keep sessions short and end before your dog becomes tense or fixated',
    ],
),
  guide(
'managing-conflict-in-multi-dog-households',
'Managing Conflict in Multi-Dog Households',
'Ease Tension Between {pet} and Other Dogs',
'Support positive relationships and reduce tension between dogs in the home',
[
      'Feed meals and offer high-value chews in separate spaces',
      'Watch for signs of tension around doorways, furniture, and people',
      'Use gates and separate spaces to keep interactions safe and give each dog regular breaks',
    ],
),
  guide(
'paired-relaxation-for-dogs',
'Paired Relaxation for Dogs',
'Teach {pet} to Relax Around Other Dogs',
'Build calm between two dogs sharing the same space',
[
      'Give each dog a defined mat or bed a comfortable distance apart',
      'Reward both dogs for staying settled rather than interacting',
      'Keep sessions short and use leashes or gates while you build the habit',
    ],
),
  guide(
'resource-guarding',
'Understanding & Managing Resource Guarding',
'Reduce {pet}\'s Need to Resource Guard',
'Build trust and reduce tension around food, toys, and space',
[
      'Give your dog an undisturbed place to eat and chew',
      'Recognize early signals like stiffening, freezing, or a hard stare',
      'Trade up for a better item instead of taking things away',
    ],
),
  guide(
'enrichment',
'Enrichment',
'Keep {pet} Engaged & Enriched',
'Keep your dog mentally engaged and fulfilled',
[
      'Rotate toys and food puzzles to keep them interesting',
      'Provide daily opportunities for sniffing, chewing, and licking',
      'Use short, reward-based training sessions for mental enrichment',
    ],
),
  guide(
'setting-your-dog-up-for-successful-rehoming',
'Setting Your Dog Up for Successful Rehoming',
'Set {pet} Up for a Smooth Transition',
'Prepare your dog and yourself for a smooth transition',
[
      'Gather vet records, feeding routine, and behavior notes in one place',
      'Write an honest listing so adopters arrive prepared',
      'Keep routines steady until adoption day',
    ],
),
];

export const CAT_GUIDES: PreSurrenderGuide[] = [
  guide(
'understanding-and-managing-handling-sensitivity',
'Understanding & Managing Handling Sensitivity',
'Build {pet}\'s Comfort With Being Handled',
'Recognize and prevent overstimulation during interaction',
[
      'Keep petting sessions short and let your cat opt in',
      'Watch for tail flicking, skin twitching, or ears turning back',
      'Redirect biting to toys rather than hands',
    ],
),
  guide(
'keeping-cats-and-kids-safe',
'Keeping Cats & Kids Safe',
'Help {pet} and Kids Feel Safe Together',
'Support safe, respectful interactions between kids and cats',
[
      'Teach kids to let your cat choose when to approach and interact',
      'Give your cat a quiet, kid-free space to retreat to',
      'Watch for signs your cat needs space, like hiding, swatting, or tail flicking',
    ],
),
  guide(
'litter-box-issues',
'Solving Litter Box Issues',
'Get {pet} Back on Track With the Litter Box',
'Set up a litter box routine that works for your cat',
[
      'Talk to your veterinarian to rule out medical causes for accidents',
      'Provide one litter box per cat, plus one extra',
      'Scoop daily and keep boxes in quiet, easy-to-reach spots',
    ],
),
  guide(
'keeping-your-indoor-cat-happy',
'Keeping Your Indoor Cat Happy',
'Help {pet} Thrive Indoors',
'Safely support your cat’s curiosity about the outdoors',
[
      'Try a catio, harness training, or a secure window perch',
      'Prevent door dashing with a routine at the entryway',
      'Add window views and bird feeders for stimulation',
    ],
),
  guide(
'excessive-meowing',
'Managing Excessive Meowing',
'Help {pet} Meow Less',
'Reduce nighttime activity and manage vocalization',
[
      'Talk to your veterinarian to rule out medical causes for increased meowing',
      'Add an active play session followed by a small meal before bed',
      'Avoid reinforcing nighttime meowing with attention',
    ],
),
  guide(
'cats-not-getting-along',
'Cats Not Getting Along',
'Ease Tension Between {pet} and Other Cats',
'Support calm, comfortable relationships between cats',
[
      'Provide separate food, water, and litter stations',
      'Add vertical space so cats can share a room without crowding',
      'Separate and reintroduce slowly after conflict',
    ],
),
  guide(
'managing-conflict-in-dog-cat-households',
'Managing Conflict in Dog-Cat Households',
'Ease Tension Between {pet} and Dogs',
'Support calm, positive interactions between dogs and cats in the home',
[
      'Give your cat high perches and dog-free rooms',
      'Use gates and closed doors to keep everyone safe',
      'Interrupt chasing or fixation early',
    ],
),
  guide(
'relaxation-for-dogs-around-cats',
'Relaxation For Dogs Around Cats',
'Help {pet} and Dogs Relax Together',
'Teach a household dog to settle in the same room as your cat',
[
      'Start with your dog at a distance where your cat can stay relaxed',
      'Reward your dog for settling calmly while your cat moves around',
      'End the session before your dog or cat becomes tense or fixated',
    ],
),
  guide(
'enrichment',
'Enrichment',
'Keep {pet} Engaged & Enriched',
'Keep your cat mentally engaged and fulfilled',
[
      'Rotate toys and food puzzles to keep them interesting',
      'Schedule short daily wand-play sessions',
      'Add scratching surfaces in the rooms your cat uses most',
    ],
),
  guide(
'setting-your-cat-up-for-successful-rehoming',
'Setting Your Cat Up for Successful Rehoming',
'Set {pet} Up for a Smooth Transition',
'Prepare your cat and yourself for a smooth transition',
[
      'Gather vet records, feeding routine, and behavior notes in one place',
      'Write an honest listing so adopters arrive prepared',
      'Keep routines steady until adoption day',
    ],
),
];

/** The two guides every pre-surrender parent sees, always last, in this order. */
const UNIVERSAL_SLUGS: Record<Species, string[]> = {
  dog: ['enrichment', 'setting-your-dog-up-for-successful-rehoming'],
  cat: ['enrichment', 'setting-your-cat-up-for-successful-rehoming'],
};

export type Species = 'dog' | 'cat';

/** Reason label (exact Typeform choice) → reason-specific guide slugs, sheet order. */
const REASON_GUIDES: Record<Species, Record<string, string[]>> = {
  dog: {
    'Aggression/Reactivity Towards Adults': ['aggression', 'understanding-and-managing-stranger-danger'],
    'Aggression/Reactivity Towards Kids': ['keeping-dogs-and-kids-safe', 'aggression'],
    'Aggression/Reactivity Towards Dogs': ['leash-reactivity'],
    'Anxiety/Separation Anxiety': ['separation-anxiety', 'building-confidence-outside-the-home'],
    Destructive: ['managing-high-energy', 'separation-anxiety'],
    'Excessive Barking/Howling': ['barking'],
    'House Soiling (Urinating/Defecating Indoors)': ['house-training'],
    'Dog-Cat Conflict': ['managing-conflict-in-dog-cat-households', 'relaxation-for-dogs-around-cats'],
    'Dog-Dog Conflict': ['managing-conflict-in-multi-dog-households', 'paired-relaxation-for-dogs'],
    "No Time/Doesn't Fit Lifestyle": ['managing-high-energy'],
    'Resource Guarding': ['resource-guarding'],
  },
  cat: {
    'Aggression Towards Adults': ['understanding-and-managing-handling-sensitivity'],
    'Aggression Towards Kids': ['keeping-cats-and-kids-safe'],
    'Litter Box Issues': ['litter-box-issues'],
    Destruction: ['keeping-your-indoor-cat-happy'],
    'Excessive Meowing/Vocalization': ['excessive-meowing'],
    'Cat-Cat Conflict': ['cats-not-getting-along'],
    'Cat-Dog Conflict': ['managing-conflict-in-dog-cat-households', 'relaxation-for-dogs-around-cats'],
    "No Time/Doesn't Fit Lifestyle": ['keeping-your-indoor-cat-happy'],
  },
};

const qGroup = (reason: string, intro: string, questions: string[]): QuestionGroup => ({ reason, intro, questions });

/** AC 6/7: shown for "Other", unrecognized free-text reasons, and no reason at all. */
export const OTHER_QUESTION_GROUP: QuestionGroup = qGroup(
'Other',
  'What kind of home would help {pet} thrive?',
[
    'What would the ideal home look like for {pet} to really thrive?',
    'What kind of household — quiet, active, with kids, with other pets — would suit {pet} best?',
    'How much time, attention, or play would {pet} need to feel their best?',
  ],
);

const REASON_QUESTIONS: Record<Species, Record<string, QuestionGroup>> = {
  dog: {
    'Aggression/Reactivity Towards Adults': qGroup(
'Aggression/Reactivity Towards Adults',
      'How does {pet} react to unfamiliar adults?',
[
        "What does {pet}'s reaction to adults look like when it happens?",
        'What have you tried to manage or work on this behavior with {pet}?',
        'What situations or types of adults tend to trigger this reaction?',
      ],
),
    'Aggression/Reactivity Towards Kids': qGroup(
'Aggression/Reactivity Towards Kids',
      'How does {pet} do around children?',
[
        "What does {pet}'s reaction to children look like when it happens?",
        'What have you tried to manage or work on this behavior with {pet}?',
        'What situations tend to trigger this reaction around children?',
      ],
),
    'Aggression/Reactivity Towards Dogs': qGroup(
'Aggression/Reactivity Towards Dogs',
      'How does {pet} react to other dogs?',
[
        "What does {pet}'s reactivity toward other dogs look like?",
        "What have you tried to manage or work on {pet}'s reactivity toward other dogs?",
        'What situations or settings tend to trigger this reactivity?',
      ],
),
    'Anxiety/Separation Anxiety': qGroup(
'Anxiety/Separation Anxiety',
      'How does {pet} handle being left alone?',
[
        'What does {pet} do when feeling anxious?',
        "What have you tried to help ease {pet}'s anxiety?",
        "What situations trigger {pet}'s anxiety?",
      ],
),
    Destructive: qGroup(
'Destructive',
      'What does {pet} tend to chew or destroy?',
[
        "What does {pet}'s destructive behavior typically look like?",
        'What have you tried to manage or redirect this behavior?',
        "What tends to trigger {pet}'s destructive behavior?",
      ],
),
    'Excessive Barking/Howling': qGroup(
'Excessive Barking/Howling',
      'How often does {pet} bark?',
[
        "How would you describe {pet}'s barking?",
        'What have you tried to manage or reduce this behavior?',
        "What situations tend to trigger {pet}'s barking or howling most?",
      ],
),
    'House Soiling (Urinating/Defecating Indoors)': qGroup(
'House Soiling (Urinating/Defecating Indoors)',
      'Is {pet} house trained?',
[
        'Has {pet} been seen by a vet recently to check for a medical cause?',
        'What have you tried to manage or resolve this behavior?',
        'When does {pet} tend to have accidents?',
      ],
),
    'Dog-Cat Conflict': qGroup(
'Dog-Cat Conflict',
      'How does {pet} do around cats?',
[
        'How does {pet} react to cats?',
        'What have you tried to manage interactions between {pet} and cats?',
        'What situations tend to trigger conflict between {pet} and cats?',
      ],
),
    'Dog-Dog Conflict': qGroup(
'Dog-Dog Conflict',
      'How does {pet} get along with other dogs in the home?',
[
        "What does {pet}'s conflict with other dogs in your home typically look like?",
        'What have you tried to manage interactions between {pet} and other dogs?',
        'What situations tend to trigger conflict between {pet} and other dogs?',
      ],
),
    "No Time/Doesn't Fit Lifestyle": qGroup(
"No Time/Doesn't Fit Lifestyle",
      'What kind of home would help {pet} thrive?',
[
        'What would the ideal home look like for {pet} to really thrive?',
        'What kind of household — quiet, active, with kids, with other pets — would suit {pet} best?',
        'How much time, attention, or exercise would {pet} need to feel their best?',
      ],
),
    'Resource Guarding': qGroup(
'Resource Guarding',
      'Does {pet} guard food, toys, or space?',
[
        'What does {pet} tend to resource guard?',
        'What have you tried to do to manage this behavior?',
        'What and who triggers the behavior?',
      ],
),
  },
  cat: {
    'Aggression Towards Adults': qGroup(
'Aggression Towards Adults',
      'How does {pet} react to unfamiliar adults?',
[
        "What does {pet}'s reaction to adults look like?",
        'What have you tried to manage or work on this behavior with {pet}?',
        'What situations tend to trigger this reaction around adults?',
      ],
),
    'Aggression Towards Kids': qGroup(
'Aggression Towards Kids',
      'How does {pet} do around children?',
[
        "What does {pet}'s reaction to children look like?",
        'What have you tried to manage or work on this behavior with {pet}?',
        'What situations tend to trigger this reaction around children?',
      ],
),
    'Litter Box Issues': qGroup(
'Litter Box Issues',
      'Is {pet} litter trained?',
[
        'Has {pet} been seen by a vet recently to check for a medical cause?',
        'What have you tried to manage or resolve this behavior?',
        'When and where do accidents tend to happen?',
      ],
),
    Destruction: qGroup(
'Destruction',
      'What does {pet} tend to scratch or destroy?',
[
        "What does {pet}'s destructive behavior typically look like?",
        'What have you tried to manage or redirect this behavior?',
        'When is {pet} destructive?',
      ],
),
    'Excessive Meowing/Vocalization': qGroup(
'Excessive Meowing/Vocalization',
      'How often does {pet} vocalize?',
[
        'How often does {pet} meow excessively?',
        'What have you tried to manage or reduce this behavior?',
        "What situations tend to trigger {pet}'s vocalizing most?",
      ],
),
    'Cat-Cat Conflict': qGroup(
'Cat-Cat Conflict',
      'How does {pet} get along with other cats?',
[
        "What does {pet}'s conflict with other cats in your home typically look like?",
        'What have you tried to manage interactions between {pet} and other cats?',
        'What situations tend to trigger conflict between {pet} and other cats?',
      ],
),
    'Cat-Dog Conflict': qGroup(
'Cat-Dog Conflict',
      'How does {pet} do around dogs?',
[
        "What does {pet}'s reaction to dogs look like when it happens?",
        'What have you tried to manage interactions between {pet} and dogs?',
        'What situations tend to trigger conflict between {pet} and dogs?',
      ],
),
    "No Time/Doesn't Fit Lifestyle": qGroup(
"No Time/Doesn't Fit Lifestyle",
      'What kind of home would help {pet} thrive?',
[
        'What would the ideal home look like for {pet} to really thrive?',
        'What kind of household — quiet, active, with kids, with other pets — would suit {pet} best?',
        'How much time, attention, or play would {pet} need to feel their best?',
      ],
),
  },
};

const guidesFor = (species: Species) => (species === 'cat' ? CAT_GUIDES : DOG_GUIDES);

/** Reported reasons that map to a known Typeform label, trimmed, order kept. */
const recognizedReasons = (species: Species, reasons: string[]) => reasons
  .map((r) => (r || '').trim())
  .filter((r) => REASON_GUIDES[species][r]);

/**
 * Resource library for the reported reasons: de-duplicated union of each
 * recognized reason's guides, in reported order, with the two universal guides
 * (Enrichment, then Setting Your Dog/Cat Up for Successful Rehoming) always
 * last. "Other", free-text, or no reasons contribute nothing beyond the
 * universal pair (AC 6/7).
 */
export function resolveGuides(species: Species, reasons: string[]): PreSurrenderGuide[] {
  const catalog = new Map(guidesFor(species).map((g) => [g.slug, g]));
  const universal = UNIVERSAL_SLUGS[species];
  const slugs: string[] = [];
  recognizedReasons(species, reasons).forEach((reason) => {
    REASON_GUIDES[species][reason].forEach((slug) => {
      if (!slugs.includes(slug) && !universal.includes(slug)) slugs.push(slug);
    });
  });
  return [...slugs, ...universal].map((slug) => catalog.get(slug) as PreSurrenderGuide);
}

/**
 * Adopter question groups: one per reported reason (never de-duplicated), in
 * reported order. Any "Other"/unrecognized reason — or no reasons at all —
 * yields the single universal group, appended last at most once — and only
 * when no reported reason already asks the same question ("No Time/Doesn't
 * Fit Lifestyle" shares the Other group's intro; QA: no duplicate content).
 */
export function resolveQuestionGroups(species: Species, reasons: string[]): QuestionGroup[] {
  const recognized = recognizedReasons(species, reasons);
  const groups = recognized.map((reason) => REASON_QUESTIONS[species][reason]);
  const hasOther = reasons.length === 0
    || reasons.some((r) => !REASON_GUIDES[species][(r || '').trim()]);
  const alreadyAsked = groups.some((g) => g.intro === OTHER_QUESTION_GROUP.intro);
  if (hasOther && !alreadyAsked) groups.push(OTHER_QUESTION_GROUP);
  return groups;
}
